/**
 * Générateur — Connaître les liens entre les unités de numération
 * Attendu ID : 597 | Niveau : 6ème | Domaine : NC
 *
 * Types générés : qcm_unique, qcm_multiple, texte, texte_trou_drag,
 *                 texte_trou_saisie, relier, classer, tableau_vf
 *
 * Labels :
 *   - Trouver le nombre d'unités de numération contenues dans un nombre
 *   - Tableau chiffre d'un rang vs nombre d'unités contenues
 *   - Passer d'une écriture en mots à l'écriture décimale
 *   - Décomposer un décimal en somme pondérée
 *   - Convertir entre unités de numération
 *
 * Variantes estimées : >400
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou, makeRelier, makeClasser,
} from '../../../utils';

// ─── Constantes ────────────────────────────────────────────────────────────

const ATTENDU_ID = 597;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ─── Rangs et logique de numération ────────────────────────────────────────

const RANG_EXP = {
    milliers: 3, centaines: 2, dizaines: 1, 'unités': 0,
    'dixièmes': -1, 'centièmes': -2, 'millièmes': -3,
};

/** Chiffre au rang donné (manipulation de chaîne, pas de float) */
const digitAtRang = (numStr, rang) => {
    const s = numStr.replace(/ /g, '');
    const [intStr, decStr = ''] = s.split(',');
    const e = RANG_EXP[rang];
    if (e >= 0) {
        const pos = intStr.length - 1 - e;
        return pos >= 0 && pos < intStr.length ? parseInt(intStr[pos]) : 0;
    }
    const pos = Math.abs(e) - 1;
    return pos >= 0 && pos < decStr.length ? parseInt(decStr[pos]) : 0;
};

/**
 * Nombre d'unités du rang contenues dans numStr.
 * Ex: nombreDeRang("3,47", "centièmes") = 347
 * Ex: nombreDeRang("591", "dizaines") = 59
 */
const nombreDeRang = (numStr, rang) => {
    const s = numStr.replace(/ /g, '');
    const [intStr, decStr = ''] = s.split(',');
    const e = RANG_EXP[rang];
    if (e >= 0) {
        if (e === 0) return parseInt(intStr) || 0;
        const truncated = intStr.slice(0, -e);
        return truncated ? parseInt(truncated) : 0;
    }
    // Décimal : combiner partie entière + N premiers chiffres décimaux
    const nDec = Math.abs(e);
    const combined = intStr + (decStr + '0'.repeat(nDec)).slice(0, nDec);
    return parseInt(combined) || 0;
};

/**
 * Pioche N propositions {text, ans} en garantissant au moins 1 V et 1 F.
 * Si le pool n'a qu'un seul type, retourne une pioche libre (cas marginal).
 */
const pickMixVF = (pool, n) => {
    const vrais = pool.filter(c => c.ans === 0);
    const faux = pool.filter(c => c.ans === 1);
    if (vrais.length === 0 || faux.length === 0) return shuffle(pool).slice(0, n);
    const nbVrai = randInt(1, Math.min(vrais.length, n - 1));
    const nbFaux = n - nbVrai;
    const pickedV = shuffle(vrais).slice(0, nbVrai);
    const pickedF = shuffle(faux).slice(0, Math.min(nbFaux, faux.length));
    return shuffle([...pickedV, ...pickedF]);
};

// ─── Pools de données ──────────────────────────────────────────────────────

// ─── Générateurs dynamiques de nombres (remplacent les anciens pools statiques) ───

/** Génère un nombre à 2 décimales (partie décimale non nulle). Ex: '3,47', '0,80'. */
const genNombre2Dec = () => {
    const entier = randInt(0, 9);
    let d1, d2;
    do {
        d1 = randInt(0, 9);
        d2 = randInt(0, 9);
    } while (d1 === 0 && d2 === 0); // éviter X,00
    return `${entier},${d1}${d2}`;
};

/** Génère un nombre à 1 décimale (partie décimale non nulle). Ex: '3,7', '0,4'. */
const genNombre1Dec = () => {
    const entier = randInt(0, 9);
    const dec = randInt(1, 9); // éviter X,0
    return `${entier},${dec}`;
};

/** Génère un nombre à 2 décimales pour conversion (peut avoir 0 en tête). */
const genNombre2DecConv = () => genNombre2Dec(); // identique pour l'instant

/**
 * Génère un décimal avec longueurs variables partie entière/décimale.
 * Défaut : partie entière 1-3 chiffres, décimale 2-4 chiffres (≈ 100 000 variantes).
 * Formatage milliers automatique pour intLen > 3.
 */
const genNombreDecimalVaried = ({ intMin = 1, intMax = 3, decMin = 2, decMax = 4 } = {}) => {
    const intLen = randInt(intMin, intMax);
    const decLen = randInt(decMin, decMax);
    const intDigits = [String(randInt(1, 9))];
    for (let i = 1; i < intLen; i++) intDigits.push(String(randInt(0, 9)));
    let decDigits;
    do {
        decDigits = Array.from({ length: decLen }, () => String(randInt(0, 9)));
    } while (decDigits.every(d => d === '0'));
    const intStr = intDigits.join('');
    const intFormatted = intStr.length > 3
        ? intStr.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
        : intStr;
    return `${intFormatted},${decDigits.join('')}`;
};

/** Mots décimaux : [en lettres, écriture décimale, rang source] */
const POOL_MOTS = [
    ['quatre centièmes', '0,04', 'centièmes'],
    ['vingt-sept millièmes', '0,027', 'millièmes'],
    ['trente-cinq centièmes', '0,35', 'centièmes'],
    ['six dixièmes', '0,6', 'dixièmes'],
    ['six centièmes', '0,06', 'centièmes'],
    ['six millièmes', '0,006', 'millièmes'],
    ['soixante-trois centièmes', '0,63', 'centièmes'],
    ['cinq millièmes', '0,005', 'millièmes'],
    ['quatre-vingt-cinq centièmes', '0,85', 'centièmes'],
    ['deux dixièmes', '0,2', 'dixièmes'],
    ['quinze centièmes', '0,15', 'centièmes'],
    ['sept millièmes', '0,007', 'millièmes'],
    ['trente millièmes', '0,030', 'millièmes'],
    ['neuf centièmes', '0,09', 'centièmes'],
    ['huit dixièmes', '0,8', 'dixièmes'],
    ['trois dixièmes', '0,3', 'dixièmes'],
    ['sept dixièmes', '0,7', 'dixièmes'],
    ['dix-sept centièmes', '0,17', 'centièmes'],
    ['vingt-deux centièmes', '0,22', 'centièmes'],
    ['cinquante-quatre centièmes', '0,54', 'centièmes'],
    ['soixante-dix-neuf centièmes', '0,79', 'centièmes'],
    ['quatre-vingt-douze centièmes', '0,92', 'centièmes'],
    ['douze millièmes', '0,012', 'millièmes'],
    ['cent vingt-cinq millièmes', '0,125', 'millièmes'],
    ['quarante-six millièmes', '0,046', 'millièmes'],
];

/** Génère une décomposition en somme pondérée : [nombre, coefficients]. */
const genDecomp = () => {
    const u = randInt(1, 9);   // unités ≥ 1 pour avoir un vrai nombre
    const d = randInt(0, 9);   // dixièmes
    const c = randInt(0, 9);   // centièmes
    // Éviter partie décimale entièrement nulle (ex: 5,00 n'est pas intéressant)
    if (d === 0 && c === 0) return genDecomp();
    const nombre = `${u},${d}${c}`;
    return [nombre, [[u, 'unités'], [d, 'dixièmes'], [c, 'centièmes']]];
};

const RANG_POIDS = {
    milliers: '1 000', centaines: '100', dizaines: '10', 'unités': '1',
    'dixièmes': '0,1', 'centièmes': '0,01', 'millièmes': '0,001',
};

// ─── Helpers spécifiques ───────────────────────────────────────────────────

/** Distracteurs pour exercice mots → décimal */
const motsDistractors = ([, correct, rang]) => {
    const distractors = [];
    const parts = correct.split(',');
    if (rang === 'dixièmes') {
        distractors.push(`0,0${correct.replace('0,', '')}`);
    } else if (rang === 'centièmes') {
        distractors.push(parts[1]?.length > 1 ? `${parts[0]},${parts[1][1]}` : `${parts[0]},${parts[1]}0`);
    } else if (rang === 'millièmes') {
        distractors.push(`0,${parts[1]?.slice(1) || '0'}`);
    }
    const digits = correct.replace('0,', '').replace(/^0+/, '') || '0';
    distractors.push(digits);
    if (rang === 'millièmes') distractors.push(`0,0${parts[1]}`);
    else if (rang === 'centièmes') distractors.push(`0,00${(parts[1] || '').replace(/^0+/, '') || '0'}`);
    else distractors.push(parts[1] ? `${parts[0]},0${parts[1]}` : `${parts[0]},00`);
    return [...new Set(distractors)].filter(d => d !== correct).slice(0, 3);
};

/** Chaîne de décomposition pondérée : "3 × 1 + 7 × 0,1 + 4 × 0,01" */
const decompStr = (coeffs) =>
    coeffs.map(([c, r]) => `${c} × ${RANG_POIDS[r]}`).join(' + ');

/** Conversion string-based : décale la virgule de N positions vers la droite (zéro FP). */
const convert = (numStr, rangCible) => {
    const nDec = Math.abs(RANG_EXP[rangCible]); // 1 / 2 / 3
    const [intStr, decStr = ''] = numStr.replace(/ /g, '').split(',');
    const padded = decStr.padEnd(nDec, '0');
    const intPart = String(parseInt(intStr + padded.slice(0, nDec)) || 0);
    const tail = padded.slice(nDec);
    return tail ? `${intPart},${tail}` : intPart;
};

// ─── EXERCICE 1 : Nombre d'unités contenues ───────────────────────────────

const L1 = "Trouver le nombre d'unités de numération contenues dans un nombre";

const RANG_ADJ = { 'dixièmes': 'centièmes', 'centièmes': 'dixièmes', 'millièmes': 'centièmes' };
const RANGS_DECIMAUX = ['dixièmes', 'centièmes', 'millièmes'];

const genNbUnitesQcm = () => {
    const num = genNombreDecimalVaried();
    const rang = randomChoice(RANGS_DECIMAUX);
    const correct = String(nombreDeRang(num, rang));
    const chiffre = String(digitAtRang(num, rang));
    const nbreAdj = String(nombreDeRang(num, RANG_ADJ[rang]));
    const s = num.replace(/ /g, '');
    const dec = s.split(',')[1] || '';
    const nDec = Math.abs(RANG_EXP[rang]); // 1, 2 ou 3
    const partieDecStr = String(parseInt(dec.slice(0, nDec)) || 0);
    let choices = [...new Set([correct, chiffre, nbreAdj, partieDecStr])].slice(0, 4);
    while (choices.length < 4) choices.push(String(parseInt(correct) + choices.length));
    const texte = `<p>Combien de ${rang} y a-t-il dans le nombre <strong>${num}</strong> ?</p>`;
    return makeQcm(choices, correct, texte, opts(L1));
};

const genNbUnitesTexte = () => {
    const num = genNombreDecimalVaried();
    const rang = randomChoice(RANGS_DECIMAUX);
    const correct = String(nombreDeRang(num, rang));
    const texte = `<p>Dans le nombre <strong>${num}</strong>, combien y a-t-il de ${rang} ?</p>`;
    return makeSaisie(correct, texte, opts(L1));
};

const genNbUnitesSaisie = () => {
    const num = genNombreDecimalVaried();
    // 2 rangs distincts parmi dixièmes/centièmes/millièmes
    const [rang1, rang2] = shuffle([...RANGS_DECIMAUX]).slice(0, 2);
    const nb1 = String(nombreDeRang(num, rang1));
    const nb2 = String(nombreDeRang(num, rang2));
    const phrase = `Dans le nombre ${num}, il y a {{${nb1}}} ${rang1} et {{${nb2}}} ${rang2}.`;
    const texte = '<p>Complète les phrases suivantes :</p>';
    return makeTexteTrou(phrase, texte, { ...opts(L1), mode: 'saisie' });
};

const genNbUnitesTableauVf = () => {
    const num1 = genNombreDecimalVaried();
    const num2 = genNombreDecimalVaried();
    const rang1 = randomChoice(RANGS_DECIMAUX);
    const rang2 = randomChoice(RANGS_DECIMAUX);
    // Factory : la vérité se calcule dynamiquement → aucun pattern figé
    const mkStmt = (num, rang, val) => ({
        text: `Dans ${num}, il y a ${val} ${rang}`,
        ans: val === nombreDeRang(num, rang) ? 0 : 1,
    });
    const candidats = [
        // num1 : vraie + pièges (chiffre/nombre, rang voisin)
        mkStmt(num1, rang1, nombreDeRang(num1, rang1)),
        mkStmt(num1, rang1, digitAtRang(num1, rang1)),
        mkStmt(num1, rang1, nombreDeRang(num1, RANG_ADJ[rang1])),
        // num2 : vraie + pièges
        mkStmt(num2, rang2, nombreDeRang(num2, rang2)),
        mkStmt(num2, rang2, digitAtRang(num2, rang2)),
        mkStmt(num2, rang2, nombreDeRang(num2, RANG_ADJ[rang2])),
    ];
    const seen = new Set();
    const pool = candidats.filter(c => !seen.has(c.text) && seen.add(c.text));
    const chosen = pickMixVF(pool, 4);
    const lignes = chosen.map(c => c.text);
    const bonnesReponses = chosen.map(c => c.ans);
    const texte = '<p>Vrai ou faux ?</p>';
    return makeVraiFaux(lignes, bonnesReponses, texte, opts(L1));
};

// ─── EXERCICE 2 : Chiffre vs nombre d'unités ──────────────────────────────

const L2 = "Tableau chiffre d'un rang vs nombre d'unités contenues";

const genChiffreNbreQcmMulti = () => {
    const num = genNombre2Dec();
    const chiffDix = digitAtRang(num, 'dixièmes');
    const chiffCent = digitAtRang(num, 'centièmes');
    const nbDix = nombreDeRang(num, 'dixièmes');
    const nbCent = nombreDeRang(num, 'centièmes');
    const correct = [
        `Le chiffre des dixièmes est ${chiffDix}`,
        `Il y a ${nbCent} centièmes dans ${num}`,
        `Le chiffre des centièmes est ${chiffCent}`,
    ];
    const wrong = [
        `Il y a ${nbDix} centièmes dans ${num}`,  // piège rang: nbDix au lieu de nbCent
        `Il y a ${nbCent} dixièmes dans ${num}`,  // piège rang: nbCent au lieu de nbDix
    ];
    const allChoices = [...correct, ...wrong];
    const texte = `<p>Pour le nombre <strong>${num}</strong>, quelles affirmations sont <strong>correctes</strong> ? (plusieurs réponses possibles)</p>`;
    return makeQcm(allChoices, correct, texte, opts(L2), true);
};

const genChiffreNbreSaisie = () => {
    const num = genNombre2Dec();
    const chiffDix = String(digitAtRang(num, 'dixièmes'));
    const nbCent = String(nombreDeRang(num, 'centièmes'));
    const phrase = `Dans ${num} : le chiffre des dixièmes est {{${chiffDix}}} et il y a {{${nbCent}}} centièmes.`;
    const texte = '<p>Complète :</p>';
    return makeTexteTrou(phrase, texte, { ...opts(L2), mode: 'saisie' });
};

const genChiffreNbreTableauVf = () => {
    const num = genNombre2Dec();
    const chiffDix = digitAtRang(num, 'dixièmes');
    const chiffCent = digitAtRang(num, 'centièmes');
    const nbDix = nombreDeRang(num, 'dixièmes');
    const nbCent = nombreDeRang(num, 'centièmes');
    // Pool de propositions — chaque item porte son texte ET sa vérité objective
    const candidats = [
        { text: `Le chiffre des dixièmes est ${chiffDix}`, ans: 0 },
        { text: `Le chiffre des dixièmes est ${chiffCent}`, ans: chiffDix === chiffCent ? 0 : 1 },
        { text: `Le chiffre des centièmes est ${chiffCent}`, ans: 0 },
        { text: `Le chiffre des centièmes est ${chiffDix}`, ans: chiffDix === chiffCent ? 0 : 1 },
        { text: `Il y a ${nbDix} dixièmes dans ${num}`, ans: 0 },
        { text: `Il y a ${nbCent} dixièmes dans ${num}`, ans: 1 }, // nbDix ≠ nbCent garanti
        { text: `Il y a ${nbCent} centièmes dans ${num}`, ans: 0 },
        { text: `Il y a ${nbDix} centièmes dans ${num}`, ans: 1 },
    ];
    // Dédup sur le texte + garantie mix V/F (min 1V et 1F sur 4 lignes)
    const seen = new Set();
    const pool = candidats.filter(c => !seen.has(c.text) && seen.add(c.text));
    const chosen = pickMixVF(pool, 4);
    const lignes = chosen.map(c => c.text);
    const bonnesReponses = chosen.map(c => c.ans);
    const texte = `<p>Vrai ou faux ? (Nombre de référence : <strong>${num}</strong>)</p>`;
    return makeVraiFaux(lignes, bonnesReponses, texte, opts(L2));
};

const genChiffreNbreClasser = () => {
    const num = genNombre2Dec();
    const chiffDix = digitAtRang(num, 'dixièmes');
    const chiffCent = digitAtRang(num, 'centièmes');
    const nbCent = nombreDeRang(num, 'centièmes');
    const nbDix = nombreDeRang(num, 'dixièmes');
    const categories = [
        { name: 'Se réfère au chiffre d\'un rang', items: [`Le chiffre des dixièmes est ${chiffDix}`, `Le chiffre des centièmes est ${chiffCent}`] },
        { name: 'Se réfère au nombre d\'unités contenues', items: [`Il y a ${nbDix} dixièmes dans ${num}`, `Il y a ${nbCent} centièmes dans ${num}`] },
    ];
    const texte = `<p>Classe chaque affirmation dans la bonne catégorie. (Nombre : <strong>${num}</strong>)</p>`;
    return makeClasser(categories, texte, opts(L2));
};

// ─── EXERCICE 3 : Mots → écriture décimale ────────────────────────────────

const L3 = "Passer d'une écriture en mots à l'écriture décimale";

const genMotsQcm = () => {
    const entry = randomChoice(POOL_MOTS);
    const [lettres, correct] = entry;
    const distractors = motsDistractors(entry);
    let choices = [...new Set([correct, ...distractors])].slice(0, 4);
    while (choices.length < 4) choices.push(`0,${'0'.repeat(choices.length)}${correct.slice(-1)}`);
    const texte = `<p>Quelle est l'écriture décimale de <em>« ${lettres} »</em> ?</p>`;
    return makeQcm(choices, correct, texte, opts(L3));
};

const genMotsTexte = () => {
    const [lettres, correct] = randomChoice(POOL_MOTS);
    const texte = `<p>Écrire en chiffres : <em>${lettres}</em></p>`;
    return makeSaisie(correct, texte, opts(L3));
};

const genMotsDrag = () => {
    const entry = randomChoice(POOL_MOTS);
    const [lettres, correct] = entry;
    const distractors = motsDistractors(entry).slice(0, 3);
    const phrase = `${lettres.charAt(0).toUpperCase() + lettres.slice(1)} s'écrit {{${correct}}} avec des chiffres.`;
    const texte = '<p>Complète :</p>';
    return makeTexteTrou(phrase, texte, { ...opts(L3), distracteurs: distractors });
};

/** Chiffre → mot français (2 à 9, on évite "un" pour éviter le singulier). */
const CHIFFRE_MOT = ['', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf'];

const genMotsRelier = () => {
    // Série dynamique "chiffre × rangs" : un chiffre c aléatoire, ses 3 rangs décimaux
    const c = randInt(2, 9);
    const mot = CHIFFRE_MOT[c];
    const serie = [
        [`${mot} dixièmes`, `0,${c}`],
        [`${mot} centièmes`, `0,0${c}`],
        [`${mot} millièmes`, `0,00${c}`],
    ];
    // 4e paire : une entrée POOL_MOTS qui ne collisionne ni sur mots ni sur valeur
    const usedSrc = new Set(serie.map(([s]) => s));
    const usedTgt = new Set(serie.map(([, t]) => t));
    const fourth = shuffle([...POOL_MOTS])
        .find(([s, t]) => !usedSrc.has(s) && !usedTgt.has(t));
    const pairs = shuffle([...serie, fourth])
        .map(([mots, valeur]) => ({ source: mots, target: valeur }));
    const texte = '<p>Relier chaque expression en lettres à son écriture décimale.</p>';
    return makeRelier(pairs, texte, opts(L3));
};

// ─── EXERCICE 4 : Décomposer en somme pondérée ────────────────────────────

const L4 = 'Décomposer un décimal en somme pondérée';

const genDecompQcm = () => {
    const [num, coeffs] = genDecomp();
    const correct = decompStr(coeffs);
    // D1 : décaler tous les rangs d'un cran vers le haut
    const shiftMap = { 'unités': 'dizaines', 'dixièmes': 'unités', 'centièmes': 'dixièmes' };
    const d1 = decompStr(coeffs.map(([c, r]) => [c, shiftMap[r] || r]));
    // D2 : inverser dixièmes et centièmes
    const swapped = [coeffs[0], [coeffs[2]?.[0] || 0, coeffs[1][1]], [coeffs[1][0], coeffs[2]?.[1] || 'centièmes']];
    const d2 = decompStr(swapped);
    // D3 : traiter comme entier (centaines, dizaines, unités)
    const intCoeffs = [[coeffs[0][0], 'centaines'], [coeffs[1][0], 'dizaines'], [coeffs[2]?.[0] || 0, 'unités']];
    const d3 = decompStr(intCoeffs);
    let choices = [...new Set([correct, d1, d2, d3])].slice(0, 4);
    const texte = `<p>Quelle décomposition est correcte pour <strong>${num}</strong> ?</p>`;
    return makeQcm(choices, correct, texte, opts(L4));
};

const genDecompDrag = () => {
    const [num, coeffs] = genDecomp();
    const partsCorrect = coeffs.map(([c]) => String(c));
    const phraseParts = coeffs.map(([c, r]) => `{{${c}}} × ${RANG_POIDS[r]}`);
    const phrase = `${num} = ${phraseParts.join(' + ')}`;
    const nbDix = String(nombreDeRang(num, 'dixièmes'));
    const nbCent = String(nombreDeRang(num, 'centièmes'));
    const distracteurs = [...new Set([nbDix, nbCent])].filter(d => !partsCorrect.includes(d)).slice(0, 3);
    const texte = '<p>Complète la décomposition :</p>';
    return makeTexteTrou(phrase, texte, { ...opts(L4), distracteurs });
};

const genDecompSaisie = () => {
    const [num, coeffs] = genDecomp();
    const phraseParts = coeffs.map(([c, r]) => `{{${c}}} × ${RANG_POIDS[r]}`);
    const phrase = `${num} = ${phraseParts.join(' + ')}`;
    const texte = '<p>Complète la décomposition :</p>';
    return makeTexteTrou(phrase, texte, { ...opts(L4), mode: 'saisie' });
};

// ─── EXERCICE 5 : Convertir entre unités ──────────────────────────────────

const L5 = 'Convertir entre unités de numération';

const genConvQcm = () => {
    const num = genNombre1Dec();
    const correct = convert(num, 'dixièmes');
    const decDigit = num.includes(',') ? num.split(',')[1] : '0';
    const d1 = decDigit.replace(/^0+/, '') || '0';
    const d2 = convert(num, 'centièmes');
    const d3 = num;
    let choices = [...new Set([correct, d1, d2, d3])].slice(0, 4);
    const texte = `<p><strong>${num}</strong> exprimé en <strong>dixièmes</strong> donne :</p>`;
    return makeQcm(choices, correct, texte, opts(L5));
};

const genConvTexte = () => {
    const num = genNombre2DecConv();
    const rang = randomChoice(['centièmes', 'dixièmes']);
    const correct = convert(num, rang);
    const texte = `<p>Exprime <strong>${num}</strong> en ${rang}.</p>`;
    return makeSaisie(correct, texte, opts(L5));
};

const genConvTableauVf = () => {
    const n1 = genNombre1Dec();
    const n2 = genNombre2Dec();
    const dec1 = n1.split(',')[1] || '0';
    const dec2 = n2.split(',')[1] || '0';
    // Factory : la vérité se calcule à partir de convert(num, rang) — aucun piège hardcodé
    const mkStmt = (num, rang, val) => ({
        text: `${num} en ${rang} vaut ${val}`,
        ans: val === convert(num, rang) ? 0 : 1,
    });
    const candidats = [
        // n1 (1 décimale) — vraies
        mkStmt(n1, 'dixièmes', convert(n1, 'dixièmes')),
        mkStmt(n1, 'centièmes', convert(n1, 'centièmes')),
        mkStmt(n1, 'millièmes', convert(n1, 'millièmes')),
        // n1 — pièges (fausses sauf si intPart=0, alors la factory corrige en V)
        mkStmt(n1, 'dixièmes', dec1),                           // piège partie déc.
        mkStmt(n1, 'dixièmes', convert(n1, 'centièmes')),       // piège rang
        mkStmt(n1, 'centièmes', convert(n1, 'dixièmes')),       // piège rang
        // n2 (2 décimales) — vraies
        mkStmt(n2, 'centièmes', convert(n2, 'centièmes')),
        mkStmt(n2, 'millièmes', convert(n2, 'millièmes')),
        // n2 — pièges
        mkStmt(n2, 'centièmes', String(parseInt(dec2))),        // piège partie déc.
        mkStmt(n2, 'centièmes', convert(n2, 'dixièmes')),       // piège rang
        mkStmt(n2, 'dixièmes', convert(n2, 'centièmes')),       // piège rang
    ];
    // Dédup sur le texte + garantie mix V/F
    const seen = new Set();
    const pool = candidats.filter(c => !seen.has(c.text) && seen.add(c.text));
    const chosen = pickMixVF(pool, 4);
    const lignes = chosen.map(c => c.text);
    const bonnesReponses = chosen.map(c => c.ans);
    const texte = '<p>Vrai ou faux ?</p>';
    return makeVraiFaux(lignes, bonnesReponses, texte, opts(L5));
};

// ─── Registre et catégories ───────────────────────────────────────────────

const GENERATORS = {
    nb_unites_qcm_unique:         genNbUnitesQcm,
    nb_unites_texte:              genNbUnitesTexte,
    nb_unites_texte_trou_saisie:  genNbUnitesSaisie,
    nb_unites_tableau_vf:         genNbUnitesTableauVf,
    chiffre_nbre_qcm_multiple:    genChiffreNbreQcmMulti,
    chiffre_nbre_texte_trou_saisie: genChiffreNbreSaisie,
    chiffre_nbre_tableau_vf:      genChiffreNbreTableauVf,
    chiffre_nbre_classer:         genChiffreNbreClasser,
    mots_qcm_unique:              genMotsQcm,
    mots_texte:                   genMotsTexte,
    mots_texte_trou_drag:         genMotsDrag,
    mots_relier:                  genMotsRelier,
    decomp_qcm_unique:            genDecompQcm,
    decomp_texte_trou_drag:       genDecompDrag,
    decomp_texte_trou_saisie:     genDecompSaisie,
    conv_qcm_unique:              genConvQcm,
    conv_texte:                   genConvTexte,
    conv_tableau_vf:              genConvTableauVf,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'nb_unites', label: L1, formatsList: buildFormats('nb_unites') },
    { id: 'chiffre_nbre', label: L2, formatsList: buildFormats('chiffre_nbre') },
    { id: 'mots', label: L3, formatsList: buildFormats('mots') },
    { id: 'decomp', label: L4, formatsList: buildFormats('decomp') },
    { id: 'conv', label: L5, formatsList: buildFormats('conv') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
